import { KnownGroupRegistrationPolicy } from '@groundfloor-platform/mgmt-service-g-sdk';
import { RJSFSchema } from '@rjsf/utils';
import { JSONSchema7 } from 'json-schema';
import { registrationPolicyToLabel } from '../../../../ui/components/pure/registration-policy-badge/registration-policy-to-label.map';
import { LOGIN_METHOD_OPTIONS } from '../../../carriers/logic/add-carrier-form';
import {
  CARRIER_REVIEW_APPROVED,
  CARRIER_REVIEW_NEEDS_REVIEW,
  NEEDS_REVIEW_LABEL
} from '../../../carriers/logic/carrier-review';
import { CarrierGroupVariant } from './form';

export interface CarrierGroupFormSchemaOptions {
  variant: CarrierGroupVariant;
  /** Organisation only: the operating units a self-registration may land in. */
  units?: { id: string; name: string }[];
  /** Organisation only: without a registration target there are no policies to show. */
  hasRegistrationTarget?: boolean;
  /** Unit only: the carrier is flagged, so its review may change. */
  canApprove?: boolean;
  readOnly?: boolean;
}

function registrationPolicy(title: string): JSONSchema7 {
  return {
    type: 'string',
    title,
    default: '',
    oneOf: Object.values(KnownGroupRegistrationPolicy).map((policy) => ({
      type: 'string',
      title: registrationPolicyToLabel[policy] ?? policy,
      enum: [policy]
    }))
  };
}

export function makeCarrierGroupFormSchema({
  variant,
  units = [],
  hasRegistrationTarget = false,
  canApprove = false,
  readOnly = false
}: CarrierGroupFormSchemaOptions): RJSFSchema {
  const isOrganisation = variant === 'organisation';
  const showPolicies = !isOrganisation || hasRegistrationTarget;

  const schema: RJSFSchema = {
    properties: {
      name: { type: 'string', title: 'Display name' },
      parent: { type: 'string', title: isOrganisation ? 'Parent project' : 'Managed by', readOnly: true },
      status: { type: 'string', title: 'Status', readOnly: true },
      // One way only: read-only unless there is a flag to clear.
      ...(!isOrganisation && {
        review: {
          type: 'string',
          title: 'Review',
          default: CARRIER_REVIEW_APPROVED,
          readOnly: !canApprove,
          oneOf: [
            { type: 'string', title: NEEDS_REVIEW_LABEL, enum: [CARRIER_REVIEW_NEEDS_REVIEW] },
            { type: 'string', title: 'Approved', enum: [CARRIER_REVIEW_APPROVED] }
          ]
        }
      }),
      ...(isOrganisation
        ? {
            registrationTarget: {
              type: 'string',
              title: 'Default registration target',
              default: '',
              readOnly: units.length === 0,
              ...(units.length > 0 && {
                oneOf: units.map((unit) => ({ type: 'string', title: unit.name, enum: [unit.id] }))
              })
            }
          }
        : { image: { type: 'string', title: 'Logo' } }),
      ...(showPolicies && {
        kioskRegistrationPolicy: registrationPolicy('Kiosk/App registration'),
        qrRegistrationPolicy: registrationPolicy('QR registration')
      }),
      loginMethod: {
        type: 'string',
        title: 'Login method',
        default: '',
        oneOf: LOGIN_METHOD_OPTIONS.map((option) => ({ type: 'string', title: option.label, enum: [option.value] }))
      }
    },
    required: ['name', ...(showPolicies ? ['kioskRegistrationPolicy', 'qrRegistrationPolicy'] : [])]
  };

  if (readOnly && schema.properties) {
    Object.values(schema.properties).forEach((property) => {
      (property as JSONSchema7).readOnly = true;
    });
    return { ...schema, readOnly: true };
  }

  return schema;
}
